import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';

import { AppConfigService } from '~/core/services/appconfig.service';
import { AppConfig } from '~/core/models/appconfig';
import { 
    WellInfo, 
    SchemaInfo, 
    WellSchema, 
    EngineerSuggestion, 
    AuthTokenRequest, 
    AuthTokenResponse,
    WellsListResponse,
    WellViewDataResponse 
} from '~/models/wells.models';

@Injectable({
  providedIn: "root",
})
export class WellsApiService {
  private get config(): AppConfig { return this.appconfigService.getConfig(); }
  private tokenSubject = new BehaviorSubject<string | null>(null);
  public token$ = this.tokenSubject.asObservable();

  constructor(
    private http: HttpClient,
    private appconfigService: AppConfigService,
    private msalService: MsalService
  ) {
  }

  //*************************************************************************
  //  Authentication Methods
  //*************************************************************************
  public issueDevToken(email: string): Observable<AuthTokenResponse> {
    // Get MSAL JWT token - no longer using hardcoded wellsApiKey
    const msalToken = this.getMsalToken();
    
    console.log('[WellsApiService] issueDevToken - Using MSAL JWT token (no X-API-Key):', !!msalToken);
    
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      "Authorization": msalToken ? `Bearer ${msalToken}` : ''
    });

    const body: AuthTokenRequest = { email };

    return this.http
      .post<AuthTokenResponse>(`${this.config.apiUrl}/token/test`, body, {
        headers,
      })
      .pipe(
        map((response: any) => {
          if (response.token) {
            this.setToken(response.token);
            return response;
          }
          throw new Error("No token received");
        }),
      );
  }

  private getMsalToken(): string | null {
    // Get MSAL JWT from localStorage (set after acquireTokenSilent during login)
    const token = localStorage.getItem('wells_api_token') || this.tokenSubject.value;
    console.log('[WellsApiService] getMsalToken - Token available:', !!token);
    return token;
  }

  public setToken(token: string): void {
    this.tokenSubject.next(token);
    localStorage.setItem("wells_api_token", token);
  }
  private readonly BU_KEY = "selectedBU";
  // initialize from sessionStorage (so refresh keeps selection)
  private selectedBUSubject = new BehaviorSubject<string>(
    sessionStorage.getItem(this.BU_KEY) ?? "ABU",
  );
  // observable for components
  selectedBU$ = this.selectedBUSubject.asObservable();
  setSelectedBU(bu: string): void {
    console.log('[BU] setSelectedBU - BU changed to:', bu);
    sessionStorage.setItem(this.BU_KEY, bu); // persist
    this.selectedBUSubject.next(bu); //reactive update everywhere
  }
  getSelectedBU(): string {
    return this.selectedBUSubject.value;
  }
  // Optional helper if you ever clear selection
  clearSelectedBU(): void {
    sessionStorage.removeItem(this.BU_KEY);
    this.selectedBUSubject.next("ABU"); // or '' if you prefer “no selection”
  }

  public getToken(): string | null {
    // First check tokenSubject, then localStorage, then config.json
    const token =
      this.tokenSubject.value ||
      localStorage.getItem("wells_api_token") ||
      this.config.access_token ||
      null;
    return token;
  }

  public clearToken(): void {
    this.tokenSubject.next(null);
    localStorage.removeItem("wells_api_token");
  }

  //*************************************************************************
  //  API Methods
  //*************************************************************************
  private getAuthHeaders(): HttpHeaders {
    // Use MSAL JWT token directly - no hardcoded X-API-Key
    const msalToken = this.getMsalToken();
    
    console.log('[WellsApiService] getAuthHeaders - Using MSAL JWT token:', !!msalToken);
    
    if (!msalToken) {
      console.error('[WellsApiService] getAuthHeaders - No MSAL JWT token available');
      throw new Error('No MSAL JWT token available. Please log in first.');
    }
    
    return new HttpHeaders({
      "Content-Type": "application/json",
      "Accept": "application/json",
      "Authorization": `Bearer ${msalToken}`
    });
  }

  public getWellsList(
    page: number = 1,
    pageSize: number = 20,
    search?: string,
  ): Observable<WellsListResponse> {
    const headers = this.getAuthHeaders();

    // Build query parameters
    const params: any = {
      page: page.toString(),
      page_size: pageSize.toString(),
    };

    // Add search parameter only if provided
    if (search && search.trim()) {
      params.search = search.trim();
    }

    return this.http.get<WellsListResponse>(
      `${this.config.apiUrl}/api/wells_list`,
      {
        headers,
        params,
      },
    );
  }

  public getSchemaList(wellName: string, bu?: string): Observable<SchemaInfo[]> {
    console.log('[API] getSchemaList - well:', wellName, '| BU:', bu);
    const headers = this.getAuthHeaders();
    const params: any = {};
    let endpoint = '';
    
    // Use different endpoints based on BU
    if (bu === 'RBU') {
      // RBU uses its own endpoint with well_name parameter
      endpoint = `${this.config.apiUrl}/api/rbu/schema/list`;
      params.well_name = wellName;
    } else {
      // ABU uses the standard endpoint
      endpoint = `${this.config.apiUrl}/api/schema/list`;
      params.well_name = wellName;
      if (bu) {
        params.bu = bu;
      }
    }

    console.log('[API] getSchemaList - Endpoint:', endpoint);
    return this.http.get<SchemaInfo[]>(endpoint, {
      headers,
      params,
    }).pipe(
      tap(() => console.log('[API] getSchemaList - SUCCESS')),
      catchError(error => {
        console.error('[API] getSchemaList - FAILED. Status:', error?.status);
        throw error;
      })
    );
  }

  public getSchema(
    wellName: string,
    schemaType: string,
    normalize: boolean = true,
  ): Observable<WellSchema> {
    console.log('[API] getSchema - well:', wellName, '| schema:', schemaType);
    const headers = this.getAuthHeaders();
    const params: any = {
      well_name: wellName,
      schema_type: schemaType,
    };

    // Get selected BU from sessionStorage to determine API endpoint
    const selectedBU = sessionStorage.getItem('selectedBU') || 'ABU';
    
    // Use old endpoint for ABU, new BU-prefixed endpoint for RBU
    const apiPath = selectedBU === 'RBU' 
      ? `${this.config.apiUrl}/api/rbu/schema`
      : `${this.config.apiUrl}/api/schema`;
    
    // Add normalize parameter only for RBU
    if (selectedBU === 'RBU') {
      const normalizedSchemaType = (schemaType || '').trim();
      const wellboreSectionAliases = ['wellboresz', 'wellbore_sz', 'wellboresection', 'wellbore_section', 'WellboreSz'];

      if (wellboreSectionAliases.includes(normalizedSchemaType)) {
        params.schema_type = 'wellboreSz';
      }

      params.normalize = normalize;
    }

    console.log('[API] getSchema - BU:', selectedBU, '| Endpoint:', apiPath);
    return this.http.get<WellSchema>(apiPath, {
      headers,
      params,
    }).pipe(
      tap(() => console.log('[API] getSchema - SUCCESS | schema:', schemaType)),
      catchError(error => {
        console.error('[API] getSchema - FAILED | schema:', schemaType, '| Status:', error?.status);
        throw error;
      })
    );
  }

  public saveSuggestions(
    wellName: string,
    schemaType: string,
    userId: string,
    suggestions: EngineerSuggestion[],
  ): Observable<any> {
    const headers = this.getAuthHeaders();
    const params = {
      well_name: wellName,
      schema_type: schemaType,
      user_id: userId,
    };

    return this.http.post(
      `${this.config.apiUrl}/api/suggestions`,
      suggestions,
      {
        headers,
        params,
      },
    );
  }

  /**
   * Schema to WellView API mapping
   */
  /**
   * Schema to WellView schema mapping
   * Maps API schema names to WellView schema names
   * COMPREHENSIVE: All possible schema name variations
   */
  private readonly SCHEMA_TO_WELLVIEW_MAP: { [key: string]: string } = {
    // ===== WELL HEADER =====
    'well_header': 'wvWellHeader',
    'wellheader': 'wvWellHeader',
    'well-header': 'wvWellHeader',
    'well header': 'wvWellHeader',
    'WellHeader': 'wvWellHeader',
    
    // ===== WELLBORE =====
    'wellbore': 'wvWellbore',
    'Wellbore': 'wvWellbore',
    
    // ===== WELLBORE SECTION / SIZE =====
    'wellbore_section': 'wvWellboreSize',
    'wellboresection': 'wvWellboreSize',
    'wellbore-section': 'wvWellboreSize',
    'wellbore section': 'wvWellboreSize',
    'wellbore_sz': 'wvWellboreSize',
    'wellboresz': 'wvWellboreSize',
    'wellbore-sz': 'wvWellboreSize',
    'wellbore sz': 'wvWellboreSize',
    'WellboreSz': 'wvWellboreSize',
    'WellboreSection': 'wvWellboreSize',
    
    // ===== GEOLOGICAL HORIZON / FORMATION =====
    'geo_horizon': 'wvWellboreFormation',
    'geohorizon': 'wvWellboreFormation',
    'geo-horizon': 'wvWellboreFormation',
    'geo horizon': 'wvWellboreFormation',
    'geological-horizon': 'wvWellboreFormation',
    'geological_horizon': 'wvWellboreFormation',
    'geologicalhorizon': 'wvWellboreFormation',
    'geological horizon': 'wvWellboreFormation',
    'wellboreformation': 'wvWellboreFormation',
    'wellbore_formation': 'wvWellboreFormation',
    'wellbore-formation': 'wvWellboreFormation',
    'wellbore formation': 'wvWellboreFormation',
    'WellboreFormation': 'wvWellboreFormation',
    'GeologicalHorizon': 'wvWellboreFormation',
    
    // ===== JOBS =====
    'jobs': 'wvJob',
    'job': 'wvJob',
    'Job': 'wvJob',
    'Jobs': 'wvJob',
    
    // ===== CASING =====
    'casing': 'wvCas',
    'cas': 'wvCas',
    'Cas': 'wvCas',
    'Casing': 'wvCas',
    
    // ===== CASING COMPONENTS =====
    'casing_component': 'wvCasComp',
    'casingcomponent': 'wvCasComp',
    'casing-component': 'wvCasComp',
    'casing component': 'wvCasComp',
    'casing_components': 'wvCasComp',
    'casingcomponents': 'wvCasComp',
    'casing-components': 'wvCasComp',
    'casing components': 'wvCasComp',
    'cascomp': 'wvCasComp',
    'CasComp': 'wvCasComp',
    'CasingComponent': 'wvCasComp',
    'CasingComponents': 'wvCasComp',
    
    // ===== CEMENT =====
    'cement': 'wvCement',
    'Cement': 'wvCement',
    
    // ===== CEMENT STAGE =====
    'cement_stage': 'wvCementStage',
    'cementstage': 'wvCementStage',
    'cement-stage': 'wvCementStage',
    'cement stage': 'wvCementStage',
    'CementStage': 'wvCementStage',
    
    // ===== PERFORATION =====
    'perforation': 'wvPerforation',
    'Perforation': 'wvPerforation',
    
    // ===== OTHER IN HOLE =====
    'other_in_hole': 'wvOtherInHole',
    'otherinhole': 'wvOtherInHole',
    'other-in-hole': 'wvOtherInHole',
    'other in hole': 'wvOtherInHole',
    'OtherInHole': 'wvOtherInHole',
    
    // ===== TUBING =====
    'tubing': 'wvTubing',
    'Tubing': 'wvTubing',
    
    // ===== TUBING COMPONENTS =====
    'tubing_comp': 'wvTubingComp',
    'tubingcomp': 'wvTubingComp',
    'tubing-comp': 'wvTubingComp',
    'tubing comp': 'wvTubingComp',
    'tubing_components': 'wvTubingComp',
    'tubingcomponents': 'wvTubingComp',
    'tubing-components': 'wvTubingComp',
    'tubing components': 'wvTubingComp',
    'TubingComp': 'wvTubingComp',
    'TubingComponents': 'wvTubingComp',
    
    // ===== ROD =====
    'rod': 'wvRod',
    'Rod': 'wvRod',
    'rod_details': 'wvRod',
    'roddetails': 'wvRod',
    'rod-details': 'wvRod',
    'rod details': 'wvRod',
    'RodDetails': 'wvRod',
    
    // ===== ROD COMPONENTS =====
    'rod_comp': 'wvRodComp',
    'rodcomp': 'wvRodComp',
    'rod-comp': 'wvRodComp',
    'rod comp': 'wvRodComp',
    'rod_components': 'wvRodComp',
    'rodcomponents': 'wvRodComp',
    'rod-components': 'wvRodComp',
    'rod components': 'wvRodComp',
    'RodComp': 'wvRodComp',
    'RodComponents': 'wvRodComp'
  };

  /**
   * Schema to API endpoint mapping for prepare/execute
   * COMPREHENSIVE: All possible schema name variations to prevent missing endpoint errors
   */
  private readonly SCHEMA_TO_ENDPOINT_MAP: { [key: string]: string } = {
    // ===== WELL HEADER =====
    'wellheader': 'wellheader',
    'well_header': 'wellheader',
    'well header': 'wellheader',
    'WellHeader': 'wellheader',
    
    // ===== WELLBORE =====
    'wellbore': 'wellbore',
    'Wellbore': 'wellbore',
    
    // ===== WELLBORE SECTION / SIZE =====
    'wellbore_sz': 'wellbore_sz',
    'wellboresz': 'wellbore_sz',
    'wellbore-sz': 'wellbore_sz',
    'wellbore sz': 'wellbore_sz',
    'wellbore_section': 'wellbore_sz',
    'wellboresection': 'wellbore_sz',
    'wellbore-section': 'wellbore_sz',
    'wellbore section': 'wellbore_sz',
    'WellboreSz': 'wellbore_sz',
    'WellboreSection': 'wellbore_sz',
    
    // ===== GEOLOGICAL HORIZON / FORMATION =====
    'geo_horizon': 'geological-horizon',
    'geohorizon': 'geological-horizon',
    'geo-horizon': 'geological-horizon',
    'geo horizon': 'geological-horizon',
    'geological-horizon': 'geological-horizon',
    'geological_horizon': 'geological-horizon',
    'geologicalhorizon': 'geological-horizon',
    'geological horizon': 'geological-horizon',
    'wellbore_formation': 'geological-horizon',
    'wellboreformation': 'geological-horizon',
    'wellbore-formation': 'geological-horizon',
    'wellbore formation': 'geological-horizon',
    'WellboreFormation': 'geological-horizon',
    'GeologicalHorizon': 'geological-horizon',
    
    // ===== CASING =====
    'casing': 'casing',
    'cas': 'casing',
    'Cas': 'casing',
    'Casing': 'casing',
    
    // ===== CASING COMPONENTS =====
    'casing_component': 'casing-components',
    'casingcomponent': 'casing-components',
    'casing-component': 'casing-components',
    'casing component': 'casing-components',
    'casing_components': 'casing-components',
    'casingcomponents': 'casing-components',
    'casing-components': 'casing-components',
    'casing components': 'casing-components',
    'cascomp': 'casing-components',
    'CasComp': 'casing-components',
    'CasingComponent': 'casing-components',
    'CasingComponents': 'casing-components',
    
    // ===== CEMENT =====
    'cement': 'cement',
    'Cement': 'cement',
    
    // ===== CEMENT STAGE =====
    'cement_stage': 'cement-stage',
    'cementstage': 'cement-stage',
    'cement-stage': 'cement-stage',
    'cement stage': 'cement-stage',
    'CementStage': 'cement-stage',
    
    // ===== PERFORATION =====
    'perforation': 'perforation',
    'Perforation': 'perforation',
    
    // ===== JOBS =====
    'job': 'job',
    'jobs': 'job',
    'Job': 'job',
    'Jobs': 'job',
    
    // ===== OTHER IN HOLE =====
    'otherinhole': 'otherinhole',
    'other_in_hole': 'otherinhole',
    'other-in-hole': 'otherinhole',
    'other in hole': 'otherinhole',
    'OtherInHole': 'otherinhole',
    
    // ===== TUBING =====
    'tubing': 'tubing',
    'Tubing': 'tubing',
    
    // ===== TUBING COMPONENTS =====
    'tubing_comp': 'tubing-components',
    'tubingcomp': 'tubing-components',
    'tubing-comp': 'tubing-components',
    'tubing comp': 'tubing-components',
    'tubing_components': 'tubing-components',
    'tubingcomponents': 'tubing-components',
    'tubing-components': 'tubing-components',
    'tubing components': 'tubing-components',
    'TubingComp': 'tubing-components',
    'TubingComponents': 'tubing-components',
    
    // ===== ROD =====
    'rod': 'rod',
    'Rod': 'rod',
    'rod_details': 'rod',
    'roddetails': 'rod',
    'rod-details': 'rod',
    'rod details': 'rod',
    'RodDetails': 'rod',
    
    // ===== ROD COMPONENTS =====
    'rod_comp': 'rod-components',
    'rodcomp': 'rod-components',
    'rod-comp': 'rod-components',
    'rod comp': 'rod-components',
    'rod_components': 'rod-components',
    'rodcomponents': 'rod-components',
    'rod-components': 'rod-components',
    'rod components': 'rod-components',
    'RodComp': 'rod-components',
    'RodComponents': 'rod-components',
    
    // ===== PROPOSED SCHEMAS =====
    'proposed_tubing': 'proposed_tubing',
    'proposed_casing': 'proposed_casing',
    'proposed_cement': 'proposed_cement',
    'proposed_perforation': 'proposed_perforation'
  };

  /**
   * Get WellView data from the middleware API
   * @param schema - The schema name (e.g., 'well_header', 'casing', etc.)
   * @param wellName - The well name (for ABU)
   * @param wellIda - The well IDA/API Number (for RBU)
   * @param bu - The business unit (RBU or ABU)
   * @param idrec - Optional idrec for wellbore-specific schemas
   * @returns Observable with WellView data response
   */
  public getWellViewData(
    schema: string,
    wellName: string,
    wellIda?: string,
    bu?: string,
    idrec?: string
  ): Observable<WellViewDataResponse> {
    const headers = this.getAuthHeaders();
    
    // Get the WellView schema name from mapping
    const wvSchema = this.SCHEMA_TO_WELLVIEW_MAP[schema] || schema;
    
    // WellView schemas that require idrec parameter (with wv prefix)
    const schemasRequiringIdrec = [
      'wvWellbore',
      'wvWellboreSize',
      'wvWellboreFormation',
      // 'wvJob',
      // 'wvCas',
      // 'wvPerforation',
      // 'wvOtherInHole',
      'wvCasComp',
      'wvCementStage',
      'wvTubing',
      'wvRod'
    ];
    
    const params: any = {
      schema: wvSchema
    };
    
    // For RBU: use wellIda (API Number) if available, otherwise use wellName
    // For ABU: always use wellName  
    if (bu === 'RBU') {
      // If wellIda is provided and not undefined, use it; otherwise use wellName as fallback
      if (wellIda && wellIda !== 'undefined') {
        params.well_ida = wellIda;
      } else {
        params.well_ida = wellName; // wellName is actually the API number from the route
      }
    } else {
      // For ABU, use wellIda if provided, otherwise use wellName
      if (wellIda && wellIda !== 'undefined') {
        params.well_ida = wellIda;
      } else {
        params.well_name = wellName;
      }
    }

    // Add bu parameter if provided
    if (bu) {
      params.bu = bu;
    }
    
    // Add idrec parameter if schema requires it and idrec is provided
    if (schemasRequiringIdrec.includes(wvSchema) && idrec) {
      params.idrec = idrec;
    }
    
    return this.http.get<WellViewDataResponse>(
      `${this.config.apiUrl}/api/wellview/data`,
      {
        headers,
        params,
      },
    );
  }

  /**
   * Get wellbore ID rec data for a specific well
   * @param wellIda - The well IDA
   * @param bu - The business unit (RBU or ABU)
   * @returns Observable with wellbore ID rec data
   */
  public getWellboreIdRec(wellIda: string, bu: string): Observable<any> {
    const headers = this.getAuthHeaders();
    const params = {
      wellida: wellIda,
      bu: bu,
    };

    return this.http.get<any>(
      `${this.config.apiUrl}/api/wellview/wellbore_idrec`,
      {
        headers,
        params,
      },
    );
  }

  /**
   * Prepare schema changes for approval (handles all schemas with conditional parameters)
   * @param schema - The schema type or endpoint name (e.g., 'cement', 'cement-stage', 'casing', 'casing-components', etc.)
   * @param wellIda - The well IDA (API Number)
   * @param bu - The business unit (RBU or ABU)
   * @param idrec - Optional idrec for schemas that require it
   * @returns Observable with prepare response containing operations and metadata
   */
  public prepareSchemaChanges(
    schema: string, 
    wellIda: string, 
    bu: string, 
    idrec?: string, 
  ): Observable<any> {
    console.log('[API] prepareSchemaChanges - schema:', schema, '| well:', wellIda, '| BU:', bu, '| idrec:', idrec || 'none');
    const endpoint = this.SCHEMA_TO_ENDPOINT_MAP[schema];
    if (!endpoint) {
      throw new Error(`No endpoint mapping found for schema: ${schema}`);
    }

    const headers = new HttpHeaders({
      'accept': 'application/json',
      'Authorization': `Bearer ${this.getMsalToken()}`
    });
    
    // Build params in correct order: wellida, then idrec (if required), then BU
    const params: any = {
      wellida: wellIda
    };
    
    // Add idrec parameter for schemas that require it
    // Schemas requiring idrec based on wellview mapping:
    // - wellbore (idrec)
    // - wellbore_sz (idrecparent)
    // - casing (idrecwellbore)
    // - cement (idrecwellbore)
    // - job (idrecwellbore)
    // - geological-horizon (idrecparent)
    // - perforation (idrecwellbore)
    // - otherinhole (idrecwellbore)
    const schemasRequiringIdrec = [
      'wellbore', 'wellbore_sz', 'wellboresz', 'WellboreSz', 'casing', 'cas', 'Cas', 
      'cement', 'Cement', 'cement-stage', 'cementstage', 'CementStage', 
      'job', 'Job', 'jobs', 
      'geological-horizon', 'geo_horizon', 'WellboreFormation', 'wellboreformation',
      'perforation', 'Perforation', 
      'otherinhole', 'other_in_hole', 'OtherInHole',
      'tubing', 'Tubing', 'tubing-components', 'TubingComp',
      'rod', 'Rod', 'rod-components', 'RodComp',
      'casing-components', 'cascomp', 'CasComp', 'casing_component'
    ];
    
    const normalizedSchema = schema.toLowerCase().replace(/[_\s]+/g, '');
    const normalizedSchemasRequiringIdrec = schemasRequiringIdrec.map(s => s.toLowerCase().replace(/[_\s]+/g, ''));
    
    // Add idrec only if the schema requires it and idrec is provided
    if (normalizedSchemasRequiringIdrec.includes(normalizedSchema) && idrec) {
      params.idrec = idrec;
    }
    
    // Add BU
    params.bu = bu;

    return this.http.get<any>(
      `${this.config.outboundApiUrl}/api/${endpoint}/prepare`,
      {
        headers,
        params,
      },
    ).pipe(
      tap(() => console.log('[API] prepareSchemaChanges - SUCCESS | schema:', schema)),
      catchError(error => {
        console.error('[API] prepareSchemaChanges - FAILED | schema:', schema, '| Status:', error?.status);
        throw error;
      })
    );
  }

  /**
   * Execute schema changes (dynamic for all schemas except wellbore)
   * @param schema - The schema type (e.g., 'cement', 'casing', 'perforation', etc.)
   * @param payload - The complete payload from prepare API response
   * @returns Observable with execution results
   */
  public executeSchemaChanges(schema: string, payload: any): Observable<any> {
    console.log('[API] executeSchemaChanges - schema:', schema);
    const endpoint = this.SCHEMA_TO_ENDPOINT_MAP[schema];
    if (!endpoint) {
      console.error(`No endpoint mapping found for schema: ${schema}. Available endpoints:`, Object.keys(this.SCHEMA_TO_ENDPOINT_MAP));
      throw new Error(`No endpoint mapping found for schema: ${schema}`);
    }

    const headers = new HttpHeaders({
      'accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.getMsalToken()}`
    });

    return this.http.post<any>(
      `${this.config.outboundApiUrl}/api/${endpoint}/execute`,
      payload,
      { headers }
    ).pipe(
      tap(() => console.log('[API] executeSchemaChanges - SUCCESS | schema:', schema)),
      catchError(error => {
        console.error('[API] executeSchemaChanges - FAILED | schema:', schema, '| Status:', error?.status);
        throw error;
      })
    );
  }

  /**
   * Prepare well header changes for approval
   * @param wellIda - The well IDA (API Number)
   * @param bu - The business unit (RBU or ABU)
   * @returns Observable with prepare response containing operations and metadata
   */
  public prepareWellHeaderChanges(wellIda: string, bu: string): Observable<any> {
    const headers = new HttpHeaders({
      'accept': 'application/json',
      'Authorization': `Bearer ${this.getMsalToken()}`
    });
    const params = {
      wellida: wellIda,
      bu: bu
    };

    return this.http.get<any>(
      `${this.config.outboundApiUrl}/api/wellheader/prepare`,
      {
        headers,
        params,
      },
    );
  }

  /**
   * Execute well header changes
   * @param payload - The complete payload from prepare API response
   * @returns Observable with execution results
   */
  public executeWellHeaderChanges(payload: any): Observable<any> {
    const headers = new HttpHeaders({
      'accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.getMsalToken()}`
    });

    return this.http.post<any>(
      `${this.config.outboundApiUrl}/api/wellheader/execute`,
      payload,
      { headers }
    );
  }

}
